/**
 * Cuenta regresiva hasta la fecha límite del proyecto final. La lógica de
 * formato es una función PURA (testeable en vitest, sin DOM ni reloj real); el
 * componente sólo la refresca cada segundo sobre la hora del servidor.
 */
import { type Component, createSignal, onCleanup, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Clock } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";

/** Firma del traductor de `useI18n` (subconjunto usado aquí). */
type TranslateFn = (message: string | MessageDescriptor) => string;

export interface CountdownParts {
  /** ¿La fecha límite ya pasó? */
  overdue: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Milisegundos absolutos restantes (0 si vencido). */
  remainingMs: number;
}

/** Descompone el tiempo restante entre `nowMs` y `deadlineMs`. Pura. */
export function countdownParts(deadlineMs: number, nowMs: number): CountdownParts {
  const diff = deadlineMs - nowMs;
  if (diff <= 0) {
    return { overdue: true, days: 0, hours: 0, minutes: 0, seconds: 0, remainingMs: 0 };
  }
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { overdue: false, days, hours, minutes, seconds, remainingMs: diff };
}

/** Texto localizado y compacto de la cuenta regresiva. Puro (recibe `t`). */
export function formatCountdown(parts: CountdownParts, t: TranslateFn): string {
  if (parts.overdue) {
    return t(msg`Deadline passed`);
  }
  const { days, hours, minutes, seconds } = parts;
  if (days > 0) {
    return t(msg`${days}d ${hours}h left`);
  }
  if (hours > 0) {
    return t(msg`${hours}h ${minutes}m left`);
  }
  return t(msg`${minutes}m ${seconds}s left`);
}

export const Countdown: Component<{ deadlineISO: string; serverNowISO: string }> = (props) => {
  const { t } = useI18n();
  const [nowMs, setNowMs] = createSignal(Date.now());
  // Reloj local anclado al offset servidor→cliente al montar (evita depender del
  // reloj del dispositivo: base = hora del servidor + tiempo transcurrido local).
  // El anclaje se hace en onMount para leer `serverNowISO` una sola vez, de forma
  // deliberada (es un snapshot al montar; cambios posteriores del prop se ignoran
  // a propósito porque el reloj ya quedó anclado).
  onMount(() => {
    const mountedAt = Date.now();
    const serverBase = new Date(props.serverNowISO).getTime();
    setNowMs(serverBase);
    const timer = setInterval(() => setNowMs(serverBase + (Date.now() - mountedAt)), 1000);
    onCleanup(() => clearInterval(timer));
  });

  const parts = () => countdownParts(new Date(props.deadlineISO).getTime(), nowMs());

  return (
    <span
      class={`inline-flex items-center gap-1 text-sm font-semibold ${
        parts().overdue ? "text-danger" : "text-accent"
      }`}
      data-testid="final-project-countdown"
      data-overdue={parts().overdue ? "true" : "false"}
    >
      <Clock class="h-3.5 w-3.5" />
      <Show when={!parts().overdue} fallback={t(msg`Deadline passed`)}>
        {formatCountdown(parts(), t)}
      </Show>
    </span>
  );
};
